import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User, Transaction } from "@/types";
import {
  loginUser,
  createUser,
  getAuthStatus,
  getTransactions,
  initiateIntaSendPayment,
  logoutUser,
  createTransaction,
  getWalletAccount,
  withdrawFromWallet,
  type WalletAccount,
  type CreateUserInput,
  type LoginInput,
  type WithdrawalRequest,
} from "./api";

interface AppState {
  user: User | null;
  isAuthenticated: boolean;
  isGuest: boolean;
  authInitialized: boolean;
  transactions: Transaction[];
  isLoading: boolean;
  error: string | null;

  account: WalletAccount | null;

  refreshAccount: () => Promise<void>;
  refreshUser: () => Promise<void>;
  refreshAll: () => Promise<void>;
  withdrawFromWallet: (amount: number, withdrawal: WithdrawalRequest) => Promise<boolean>;

  initAuth: () => Promise<void>;
  login: (identifier: string, password: string) => Promise<boolean>;
  signup: (fullName: string, email?: string, password?: string, phone?: string) => Promise<boolean>;
  loginAsGuest: () => void;
  logoutGuest: () => void;
  logout: () => Promise<void>;
  loadUserData: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User | null) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isGuest: false,
      authInitialized: false,
      transactions: [],
      isLoading: false,
      error: null,

      account: null,

      initAuth: async () => {
        if (get().authInitialized) return;
        set({ isLoading: true });
        try {
          const data = await getAuthStatus();
          set({
            user: data.user ?? null,
            isAuthenticated: !!data.user,
            isGuest: false,
            isLoading: false,
            authInitialized: true,
          });
          if (data.user) {
            await get().loadUserData();
          }
        } catch (e) {
          // Network error or session invalid. Keep the persisted user state
          // intact (if any) so a transient network failure doesn't log the
          // user out mid-session. The session will be re-checked on next
          // protected-route access via authGuard.
          set({
            isLoading: false,
            authInitialized: true,
          });
        }
      },

      login: async (identifier: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const data = await loginUser({ identifier: identifier.trim(), password });
          set({
            user: data.user,
            isAuthenticated: true,
            isGuest: false,
            isLoading: false,
            authInitialized: true,
          });
          await get().loadUserData();
          return true;
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : "Login failed",
            isLoading: false,
          });
          return false;
        }
      },

      signup: async (fullName: string, email?: string, password?: string, phone?: string) => {
        set({ isLoading: true, error: null });
        try {
          const input: CreateUserInput = { fullName: fullName.trim(), password: password || "" };

          if (email) {
            input.email = email.trim().toLowerCase();
          }

          if (phone) {
            input.phone = phone.replace(/\D/g, "");
          }

          const data = await createUser(input);
          set({
            user: data.user,
            isAuthenticated: true,
            isGuest: false,
            transactions: [],
            isLoading: false,
            authInitialized: true,
          });
          await get().loadUserData();
          return true;
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : "Signup failed",
            isLoading: false,
          });
          return false;
        }
      },

      loginAsGuest: () => {
        set({
          isGuest: true,
          isAuthenticated: false,
          transactions: [],
        });
      },

      logoutGuest: () => {
        set({
          isGuest: false,
          transactions: [],
        });
      },

      logout: async () => {
        try {
          await logoutUser();
        } catch (e) {
          console.error("Logout API error:", e);
        }
        set({
          user: null,
          isAuthenticated: false,
          isGuest: false,
          authInitialized: false,
          transactions: [],
          error: null,
        });
      },

      loadUserData: async () => {
        const { user } = get();

        if (user) {
          try {
            const [txData, accountData] = await Promise.all([
              getTransactions(user.id, { autoPromote: true }),
              getWalletAccount(),
            ]);
            const normalizedTransactions = txData.transactions.map((t) => ({
              ...t,
              amount: Number(t.amount) || 0,
            }));

            set({
              transactions: normalizedTransactions,
              account: accountData.account,
            });
          } catch (err) {
            console.error("Failed to load user data:", err);
          }
        }
      },

      refreshAll: async () => {
        await Promise.all([get().refreshUser(), get().refreshAccount(), get().loadUserData()]);
      },

      clearError: () => set({ error: null }),

      setUser: (user: User | null) => set({ user }),

      refreshUser: async () => {
        try {
          const data = await getAuthStatus();
          if (data.user) {
            set({ user: data.user });
          }
        } catch (err) {
          console.error("Failed to refresh user:", err);
        }
      },

      refreshAccount: async () => {
        try {
          const data = await getWalletAccount();
          set({ account: data.account });
        } catch (err) {
          console.error("Failed to load wallet account:", err);
        }
      },

      withdrawFromWallet: async (amount, withdrawal) => {
        const { account } = get();
        if (!account || amount > account.balance || amount <= 0) return false;
        try {
          await withdrawFromWallet({
            amount,
            method: withdrawal.method,
            phone: withdrawal.phone,
            fullName: withdrawal.fullName,
            bankName: withdrawal.bankName,
            accountNumber: withdrawal.accountNumber,
            paypalEmail: withdrawal.paypalEmail,
          });
          await get().refreshAccount();
          return true;
        } catch (err) {
          set({ error: err instanceof Error ? err.message : "Withdrawal failed" });
          return false;
        }
      },
    }),
    {
      name: "pata-tasks-store",
      partialize: (state) => {
        const { authInitialized, isLoading, error, ...persistedState } = state;
        return persistedState;
      },
      onRehydrateStorage: () => (rehydratedState) => {
        if (rehydratedState && !rehydratedState.authInitialized) {
          rehydratedState.initAuth();
        }
      },
    },
  ),
);
